"use client";
import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";

import "../../marken/marken.css";

import { TopSectionMarken } from "../../marken/sections/TopSection";
import { AutoVerkaufenGrid } from "@/common/AutoVerkaufenGrid";
import { Banner } from "@/common/Banner";
import sellCarCash from "@/assets/auto-verkaufen/Rückbank-autoverkauf.avif";

import höchstenPreis from "@/assets/auto-verkaufen/Frau-verkauft-ihr-auto.webp";
import BannerImage1 from "@/assets/auto-verkaufen/AutoeinfachlosBannerBig1.avif";
import BannerImage2 from "@/assets/auto-verkaufen/AutoeinfachlosBannerBig2.avif";
import BildTextInfo from "@/components/BildTextInfo/BildTextInfo";

import { HäufigeFragen } from "@/common/HäufigeFragen";
import FAQImage from "@/assets/faq-autoverkauf-autoeinfachlos.webp";
import { fragenAutoAbmelden } from "./data";

// Ratgeber SERVERSEITIG rendern (dynamic OHNE die SSR-Abschaltung, Playbook 2.6):
// die sechs "Mehr zum Thema"-Kacheln verlinken u. a. /auto-verkaufen.
const Ratgeber = dynamic(() =>
  import("@/components/Ratgeber/Ratgeber").then((m) => m.default)
);

// Only needed when the user reaches step 12; avoid loading auth-related JS/CSS on initial paint.
const AnmeldenSection = dynamic(() => import("@/app/anmelden/AnmeldenStepRedirect"), {
  ssr: false,
});

function AutoAbmelden() {
  const [step, setStep] = useState(1);
  // selected values
  const [selectedBrand, setSelectedBrand] = useState({ id: "", name: "" });
  const [selectedModel, setSelectedModel] = useState({ id: "", name: "" });
  const [selectedYear, setSelectedYear] = useState({ id: "", name: "" });
  const [selectedBauform, setSelectedBauform] = useState({ id: "", name: "" });
  const [selectedFuel, setSelectedFuel] = useState({ id: "", name: "" });
  const [selectedVariant, setSelectedVariant] = useState({ id: "", name: "" });
  const [selectedPs, setSelectedPs] = useState({ id: "", name: "" });
  const [selectedTransmission, setSelectedTransmission] = useState({
    id: "",
    name: "",
  });
  const [selectedDoors, setSelectedDoors] = useState({ id: "", name: "" });
  const [selectedModelType, setSelectedModelType] = useState({
    id: "",
    name: "",
  });
  const [selectedKmStand, setSelectedKmStand] = useState("");
  const isSecondaryMethod = true;

  useEffect(() => {
    if (step >= 10) {
      if (typeof AnmeldenSection?.preload === "function") {
        AnmeldenSection.preload();
      } else {
        import("@/app/anmelden/AnmeldenStepRedirect");
      }
    }
  }, [step]);


  return (
    <div className="home-container">
      <TopSectionMarken
        title="Auto abmelden: Ablauf und wichtige Unterlagen"
        link="/guides/auto-abmelden"
        linkname="Auto abmelden"
        text={
          <>
            84% der Autoeinfachlos-Kunden bekommen mehr Geld.*{" "}
            <br className="desktop" />
            Hol dir deine kostenlose und sofortige Bewertung.
          </>
        }
        removePadding={true}
        step={step}
        setStep={setStep}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedBauform={selectedBauform}
        setSelectedBauform={setSelectedBauform}
        selectedFuel={selectedFuel}
        setSelectedFuel={setSelectedFuel}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        selectedPs={selectedPs}
        setSelectedPs={setSelectedPs}
        selectedTransmission={selectedTransmission}
        setSelectedTransmission={setSelectedTransmission}
        selectedDoors={selectedDoors}
        setSelectedDoors={setSelectedDoors}
        setSelectedModelType={setSelectedModelType}
        selectedModelType={selectedModelType}
        selectedKmStand={selectedKmStand}
        setSelectedKmStand={setSelectedKmStand}
      />

      {step === 12 && (
        <>
          <AnmeldenSection
            title="Bitte gib deine E-Mail-Adresse ein, um fortzufahren"
            fin={null}
            isManual={isSecondaryMethod}
            marke={selectedBrand}
            model={selectedModel}
            baujahr={selectedYear}
            bodytype={selectedBauform}
            fueltype={selectedFuel}
            variant={selectedVariant}
            ps={selectedPs}
            gearbox={selectedTransmission}
            doors={selectedDoors}
            modelType={selectedModelType}
            kmStand={selectedKmStand}
            page="/auto-verkaufen"
          />
          <Banner removeContent className="Banner-anmelden" />
        </>
      )}

      {step !== 12 && (
        <div className="Page background-color">
          <div className="Page-wrapper">
            <AutoVerkaufenGrid
              image={sellCarCash}
              leftTitle="Was brauche ich, um mein Auto abzumelden?"
              leftText={
                <>
                  <p>
                    Du möchtest dein Auto abmelden, weil du es nicht mehr nutzt,
                    verkaufen willst oder ein neues Fahrzeug bekommst? Dann bist
                    du hier genau richtig.
                  </p>
                  <p>
                    In diesem Ratgeber erfährst du alles rund um das Thema Auto
                    abmelden in Österreich - von den notwendigen Unterlagen über
                    den Ablauf bis zu den Kosten. Außerdem zeigen wir dir, wie
                    du dein Fahrzeug direkt über Autoeinfachlos schnell und
                    sicher verkaufen kannst, inklusive Unterstützung bei der
                    Abmeldung.
                  </p>
                </>
              }
              rightFirstTitle="Wann muss ich mein Auto abmelden?"
              rightFirstText={
                <>
                  <p>
                    Ein Fahrzeug muss immer dann abgemeldet werden, wenn es
                    nicht mehr am Straßenverkehr teilnimmt oder den Besitzer
                    wechselt. Das betrifft zum Beispiel folgende Fälle:
                  </p>
                  <ul className="custom-unordered-list">
                    <li>
                      Du verkaufst dein Auto oder tauschst es gegen ein neues.
                    </li>
                    <li>
                      Dein Fahrzeug hat einen Totalschaden oder ist nicht mehr
                      fahrtauglich.
                    </li>
                    <li>
                      Du möchtest Versicherungskosten sparen, weil du das Auto
                      längere Zeit nicht nutzt.
                    </li>
                    <li>Du exportierst das Auto ins Ausland.</li>
                  </ul>

                  <p>In Österreich unterscheidet man zwischen zwei Formen:</p>
                  <ol className="custom-ordered-list secondary">
                    <li>
                      <strong>Abmeldung (endgültig)</strong>
                      <br />
                      Das Fahrzeug wird dauerhaft stillgelegt und aus dem
                      Verkehr gezogen. Diese Variante ist üblich bei Verkauf,
                      Verschrottung oder Export.
                    </li>
                    <li>
                      <strong>Stilllegung (vorübergehend)</strong>
                      <br />
                      Hierbei wird das Fahrzeug nur vorübergehend außer Betrieb
                      genommen. Das kann sinnvoll sein, wenn du es später wieder
                      nutzen möchtest – etwa bei saisonaler Nutzung oder
                      längerer Auslandsabwesenheit. In Österreich läuft das über
                      die Hinterlegung der Kennzeichentafeln (mehr dazu unten) –
                      nicht über eine Abmeldung.
                    </li>
                  </ol>

                  <p>
                    <strong>Tipp:</strong> Wenn du dir unsicher bist, ob du dein
                    Auto abmelden oder verkaufen sollst, kann sich ein Vergleich
                    lohnen. Eine kostenlose Bewertung auf{" "}
                    <Link className="link" href="/auto-bewerten">
                      autoeinfachlos.at/auto-bewerten
                    </Link>{" "}
                    zeigt dir, welchen Marktwert dein Fahrzeug aktuell hat.
                  </p>

                  <h3>
                    Auto abmelden – so funktioniert es Schritt für Schritt
                  </h3>
                  <p>
                    Das Abmelden deines Autos ist in Österreich unkompliziert.
                    Mit der richtigen Vorbereitung dauert der Vorgang meist nur
                    wenige Minuten.
                  </p>
                  <ol className="custom-ordered-list secondary">
                    <li>
                      <strong>Unterlagen bereitlegen</strong>
                      <br />
                      Für die Abmeldung benötigst du:
                      <ul className="custom-unordered-list">
                        <li>Zulassungsschein (Teil I)</li>
                        <li>Kennzeichentafeln</li>
                        <li>
                          Typenschein oder Zulassungsbescheinigung Teil II
                        </li>
                        <li>
                          Amtlicher Lichtbildausweis (z. B. Reisepass oder
                          Führerschein)
                        </li>
                      </ul>
                      Wenn du dein Fahrzeug über Autoeinfachlos verkaufst,
                      unterstützen wir dich auch beim Papierkram. Der Händler
                      wird alles notwendige mit dir besprechen und vorbereiten.
                    </li>
                    <li>
                      <strong>Zulassungsstelle aufsuchen</strong>
                      <br />
                      Die Unterlagen müssen einer Zulassungsstelle vorgelegt
                      werden – das geht bei jeder Zulassungsstelle in ganz
                      Österreich. Mit einer schriftlichen Vollmacht kann das
                      auch eine Vertreterin oder ein Vertreter für dich
                      erledigen. Eine reine Online-Abmeldung gibt es in
                      Österreich nicht (mehr dazu unten). Die Abmeldung wird in
                      der Regel sofort durchgeführt, und du erhältst eine
                      Bestätigung.
                    </li>
                    <li>
                      <strong>Versicherung & Steuer klären</strong>
                      <br />
                      Mit der Abmeldung endet die Zulassung – und damit die
                      Grundlage für die motorbezogene Versicherungssteuer.
                      Besprich trotzdem direkt mit deiner Kfz-Versicherung, ab
                      welchem Tag der Vertrag ruht oder endet und wie bereits
                      bezahlte Prämien behandelt werden. Hebe die
                      Abmeldebestätigung auf: Sie ist dein Nachweis gegenüber
                      Versicherung und Behörde.
                    </li>
                  </ol>
                </>
              }
              rightSecondTitle="Auto abmelden beim Verkauf – was ist zu beachten?"
              rightSecondText={
                <>
                  <p>
                    Wenn du dein Auto verkaufst, stellt sich oft die Frage:
                    <br />
                    Soll ich es vor oder nach dem Verkauf abmelden?
                  </p>

                  <p>
                    <strong>Vor dem Verkauf abmelden</strong>
                    <br />
                    Du kannst dein Fahrzeug bereits vor dem Verkauf abmelden –
                    das ist sinnvoll, wenn es nicht mehr genutzt wird. Das Auto
                    darf danach aber nicht mehr auf öffentlichen Straßen bewegt
                    werden (auch nicht zur Probefahrt).
                  </p>

                  <p>
                    <strong>Nach dem Verkauf abmelden</strong>
                    <br />
                    In vielen Fällen übernimmt der Käufer die Abmeldung. Um
                    Missverständnisse zu vermeiden, solltest du die Abmeldung
                    schriftlich vereinbaren.
                  </p>

                  <p>
                    Bei Autoeinfachlos musst du dich darum nicht kümmern: Wenn
                    du dein Fahrzeug über uns verkaufst, übernehmen wir auf
                    Wunsch die komplette Abwicklung und Abmeldung für dich. So
                    bist du auf der sicheren Seite und vermeidest unnötigen
                    Aufwand.
                  </p>
                </>
              }
            />

            <div className="Banner-sections">
              <BildTextInfo
                image={BannerImage1}
                revert
                h2
                isBig
                fontSize="36px"
                title="Kosten & Dauer der Abmeldung"
                alt="Kosten und Dauer der Auto-Abmeldung"
                text={
                  <>
                    <p>
                      Die Abmeldung eines Autos ist in Österreich kostenlos und
                      schnell erledigt: Für die Abmeldung selbst fallen keine
                      Gebühren an.
                    </p>
                    <table className="GuideDataTable">
                      <thead>
                        <tr>
                          <th>Leistung</th>
                          <th>Kosten</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Abmeldung bei der Zulassungsstelle</td>
                          <td>keine Gebühren</td>
                        </tr>
                        <tr>
                          <td>
                            Hinterlegung der Kennzeichentafeln (vorübergehend,
                            statt einer Abmeldung)
                          </td>
                          <td>keine Gebühren</td>
                        </tr>
                      </tbody>
                    </table>

                    <p>
                      <strong>Dauer:</strong>
                      <br />
                      Die Abmeldung erfolgt in der Regel sofort, sobald du mit
                      den Unterlagen und den Kennzeichentafeln bei der
                      Zulassungsstelle bist.
                    </p>

                    <h3>Kennzeichen: Freihaltung oder Hinterlegung?</h3>
                    <p>
                      Bei der Abmeldung gibst du die Kennzeichentafeln ab. Rund
                      um das Kennzeichen werden zwei Verfahren oft verwechselt –
                      sie gehören zu <strong>unterschiedlichen Wegen</strong>:
                    </p>
                    <ul className="custom-unordered-list">
                      <li>
                        <strong>Freihaltung – gehört zur Abmeldung:</strong> Im
                        Zuge der Abmeldung kannst du die Freihaltung des
                        Kennzeichens für ein anderes Fahrzeug beantragen, für
                        längstens zwölf Monate. Die Zulassung des abgemeldeten
                        Fahrzeugs endet dabei. <strong>Wichtig:</strong> Die
                        Anmeldung des neuen Fahrzeugs muss dann bei derselben
                        Zulassungsstelle erfolgen, bei der du das alte Fahrzeug
                        abgemeldet hast. Wenn du das Kennzeichen behalten
                        willst, wähle die Zulassungsstelle also mit Bedacht.
                      </li>
                      <li>
                        <strong>
                          Hinterlegung – die Alternative zur Abmeldung:
                        </strong>{" "}
                        Wenn du das Fahrzeug nur zeitweise nicht nutzt – etwa
                        über den Winter oder bei einem längeren
                        Auslandsaufenthalt – meldest du es nicht ab, sondern
                        hinterlegst die Kennzeichentafeln bei einer
                        Zulassungsstelle. Das ist bis zu einem Jahr möglich und
                        gebührenfrei. <strong>Achtung bei der Frist:</strong>{" "}
                        Spätestens vor Ablauf eines Jahres musst du die Tafeln
                        wieder abholen oder die Hinterlegung verlängern lassen.
                        Wird die Jahresfrist überschritten und keine
                        Verlängerung beantragt, erlischt die Zulassung.
                      </li>
                    </ul>

                    <p>
                      <strong>Wichtig bei der Hinterlegung:</strong> Erst wenn
                      die Kennzeichentafeln mindestens 45 Tage hinterlegt
                      wurden, ist für den Hinterlegungszeitraum keine
                      Kraftfahrzeugsteuer bzw. motorbezogene
                      Versicherungssteuer zu entrichten. Der Tag der
                      Hinterlegung und der Tag der Ausfolgung werden dabei nicht
                      mitgerechnet. Eine kürzere Hinterlegung bringt also keine
                      Steuerersparnis.
                    </p>
                  </>
                }
              />

              <BildTextInfo
                image={BannerImage2}
                h2
                isBig
                fontSize="36px"
                title="Auto online abmelden in Österreich – geht das?"
                alt="Auto abmelden in Österreich: Abmeldung bei der Zulassungsstelle statt online"
                text={
                  <>
                    <p>
                      <strong>
                        Nein – eine reine Online-Abmeldung des Autos gibt es in
                        Österreich nicht.
                      </strong>{" "}
                      Die erforderlichen Unterlagen müssen einer Zulassungsstelle
                      vorgelegt werden. Dazu gehören auch die
                      Kennzeichentafeln, und die lassen sich nicht digital
                      übermitteln.
                    </p>

                    <p>
                      Das gilt auch mit <strong>ID Austria</strong>: Die ID
                      Austria ist der digitale Ausweis für viele
                      Amtswege – ein Online-Verfahren für die Kfz-Abmeldung
                      ersetzt sie nicht.
                    </p>

                    <p>
                      <strong>
                        Warum im Netz trotzdem etwas anderes steht:
                      </strong>{" "}
                      Viele Ratgeber beschreiben den deutschen Ablauf. In
                      Deutschland gibt es eine Online-Außerbetriebsetzung mit
                      Sicherheitscodes auf den Fahrzeugpapieren. Dieses
                      Verfahren gibt es in Österreich nicht – wer sich darauf
                      verlässt, steht am Ende doch bei der Zulassungsstelle.
                    </p>

                    <p>
                      <strong>
                        Wenn du selbst keine Zeit hast – die echte Alternative:
                      </strong>
                    </p>
                    <ul className="custom-unordered-list">
                      <li>
                        <strong>Vollmacht:</strong> Mit einer schriftlichen
                        Vollmacht kann eine Vertreterin oder ein Vertreter das
                        Fahrzeug für dich abmelden.
                      </li>
                      <li>
                        <strong>Freie Wahl der Stelle:</strong> Die Abmeldung
                        ist bei jeder Zulassungsstelle in ganz Österreich
                        möglich – du bist nicht an deinen Wohnbezirk gebunden.
                        Willst du dein Kennzeichen freihalten lassen, beachte,
                        dass du das neue Fahrzeug bei genau dieser
                        Zulassungsstelle anmelden musst.
                      </li>
                      <li>
                        <strong>Beim Verkauf über Autoeinfachlos:</strong> Auf
                        Wunsch übernehmen wir die Abwicklung inklusive
                        Abmeldung, dann musst du dich um den Termin gar nicht
                        kümmern.
                      </li>
                    </ul>

                    <p>
                      Online vorbereiten lässt sich der Termin trotzdem:
                      Unterlagen zusammensuchen, Öffnungszeiten und
                      Terminvergabe der Zulassungsstelle prüfen. Der Termin
                      selbst dauert dann meist nur wenige Minuten – und die
                      Abmeldung ist gebührenfrei.
                    </p>
                  </>
                }
              />
            </div>

            <HäufigeFragen
              title="Häufige Fragen zum Thema Auto abmelden"
              fragen={fragenAutoAbmelden}
              image={FAQImage}
              removePaddingBottom
            />

            <div style={{ paddingTop: "120px" }}>
              <Ratgeber
                title="Mehr zum Thema Auto abmelden"
                kaputtesAuto
                unfallAuto
                autoExport
                autoVerkaufen
                autoBewerten
                listenpreisBerechnen
              />
            </div>

            <div className="Banner-sections-marken">
              <BildTextInfo
                image={höchstenPreis}
                h2
                fontSize="36px"
                title="Auto abmelden oder gleich verkaufen?"
                alt="Frau sitzt in einem Auto und nutzt ihr Smartphone, um mit Autoeinfachlos ihr Auto zu verkaufen."
                text={
                  <>
                    <p>
                      Das Abmelden eines Autos ist in Österreich einfach,
                      kostenlos und schnell erledigt. Trotzdem lohnt es sich oft,
                      statt einer Stilllegung gleich über einen Verkauf
                      nachzudenken, besonders, wenn du das Fahrzeug ohnehin
                      nicht mehr nutzt.
                    </p>
                    <p>
                      Ein ungenutztes Auto verliert Monat für Monat an Wert und
                      verursacht laufende Kosten. Mit Autoeinfachlos kannst du
                      dein Fahrzeug einfach, sicher und zu einem fairen Preis
                      verkaufen - inklusive Abholservice und Abmeldung auf
                      Wunsch.
                    </p>
                  </>
                }
              />
            </div>
          </div>
          <Banner
            link="/auto-verkaufen"
            title="Jetzt Auto kostenlos bewerten & sicher verkaufen"
          />
        </div>
      )}
    </div>
  );
}

export default AutoAbmelden;
